import React from "react";
import ProfileImage from "./ProfileImage";
import { Delete, Edit, Pencil, Trash2, Play } from "lucide-react";
import Icon from "components/AppIcon";

const SOCIAL_LINK_KEYS = ["facebook", "instagram", "linkedin", "website", "twitter"];
const stripHtml = (html = "") =>
  typeof html === "string"
    ? html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()
    : "";

const DataTable = ({
  columns,
  data,
  isLoading,
  error,
  handleSwitch,
  sortConfig,
  onSort,
  onRetry,
  emptyMessage,
  currentPage,
  itemsPerPage,
  onAction,
}) => {
  const renderHeader = () => (
    <thead className="bg-gray-50 whitespace-nowrap">
      <tr>
        {columns.map((col) => (
          <th
            key={col.key}
            className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${col.sortable ? "cursor-pointer select-none" : ""
              }`}
            onClick={() => col.sortable && onSort(col.key)}
          >
            {col.label}
          </th>
        ))}
      </tr>
    </thead>
  );

  const renderRows = () => (
    <tbody className="bg-white divide-y divide-gray-200 whitespace-nowrap">
      {data.map((item, index) => (
        <tr key={index} className="hover:bg-gray-50">
          {columns.map((col) => {
            let value;
            if (col.key === "sno") value = index + 1 + (currentPage - 1) * itemsPerPage;
            else if (col.key === "createdAt")
              value = new Date(item[col.key]).toLocaleDateString();
            else if (col.key === "images") {
              // Handle both string and array formats
              const fileUrls = item[col.key]
                ? (Array.isArray(item[col.key]) ? item[col.key] : [item[col.key]])
                : [];

              if (fileUrls.length === 0) {
                value = <span className="text-gray-400">-</span>;
              } else {
                const firstUrl = fileUrls[0];
                const isVideo = firstUrl?.includes("mp4") ||
                  firstUrl?.includes("webm") ||
                  firstUrl?.includes("video");
                const hasMultiple = fileUrls.length > 1;

                value = (
                  <div
                    className="relative cursor-pointer group w-20 h-20"
                    onClick={() => onAction(item, "viewMedia")}
                  >
                    <div className="w-full h-full rounded border-2 border-gray-200 overflow-hidden hover:border-primary-600 transition-colors">
                      {isVideo ? (
                        <div className="w-full h-full bg-gray-100 flex items-center justify-center relative">
                          <video
                            src={firstUrl}
                            className="w-full h-full object-cover"
                            muted
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                            <Play className="w-6 h-6 text-white" />
                          </div>
                        </div>
                      ) : (
                        <ProfileImage imageUrl={firstUrl} className="w-full h-full" />
                      )}
                    </div>
                    {hasMultiple && (
                      <div className="absolute -top-1 -right-1 bg-primary-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-semibold">
                        {fileUrls.length}
                      </div>
                    )}
                    <div className="absolute z-10 bottom-0 left-0 right-0 w-full h-full rounded transition-opacity pointer-events-none">
                      <div className="flex items-center justify-center w-full  h-full">
                        <span className="text-white text-xs opacity-0 group-hover:opacity-100 font-medium">
                          Click to view
                        </span>
                      </div>
                    </div>
                  </div>
                );
              }
            } else if (col.key === "title" || col.key === "subTitle") {
              const textValue = item[col.key] ?? "";
              value = textValue ? (
                <span
                  className="block max-w-[220px] truncate"
                  title={stripHtml(textValue)}
                >
                  {stripHtml(textValue)}
                </span>
              ) : (
                <span className="text-gray-400">-</span>
              );
            } else if (col.key === "description" || col.key === "shortDescription") {
              const fullText = stripHtml(item[col.key]);
              value = fullText ? (
                <button
                  type="button"
                  onClick={() => onAction(item)}
                  className="text-left text-primary-600 hover:text-primary-700 focus:outline-none"
                  title={fullText}
                >
                  <span className="block max-w-[260px] truncate">{fullText}</span>
                </button>
              ) : (
                <span className="text-gray-400">-</span>
              );
            } else if (col.key === "keywords") {
              const keywords = Array.isArray(item[col.key]) ? item[col.key] : [];
              const keywordsLength = keywords.length;
              const firstKeyword = keywordsLength > 0 ? keywords[0] : null;

              if (keywordsLength === 0) {
                value = <span className="text-gray-400">-</span>;
              } else {
                value = (
                  <span
                    className="cursor-pointer text-primary-600 underline"
                    onClick={() => onAction(item, "viewKeywords")}
                  >
                    {firstKeyword} {keywordsLength > 1 && `(+${keywordsLength - 1} more)`}
                  </span>
                );
              }
            } else if (col.key === "socialLinks") {
              const socialLinks = item[col.key] || {};
              const linkCount = SOCIAL_LINK_KEYS.reduce((count, key) => {
                const rawValue = socialLinks[key] ?? item[key] ?? "";
                const value = typeof rawValue === "string" ? rawValue.trim() : String(rawValue || "").trim();
                return value ? count + 1 : count;
              }, 0);

              if (linkCount === 0) {
                value = <span className="text-gray-400">-</span>;
              } else {
                value = (
                  <span
                    className="cursor-pointer text-primary-600 underline"
                    onClick={() => onAction(item, "viewSocialLinks")}
                  >
                    View Links ({linkCount})
                  </span>
                );
              }
            } else if (col.key === "titlePosition") {
              const position = item[col.key];
              value = position ? (
                <span className="block max-w-[220px] truncate" title={position === "top" ? "Top" : "Bottom"}>
                  {position === "top" ? "Top" : position === "bottom" ? "Bottom" : position}
                </span>
              ) : (
                <span className="text-gray-400">-</span>
              );
            } else if (col.key === "titleColor" || col.key === "categoryColor") {
              const colorValue = item[col.key];
              value = colorValue ? (
                <div className="flex items-center gap-2">
                  <div
                    className="w-6 h-6 rounded border border-gray-300"
                    style={{ backgroundColor: colorValue }}
                    title={colorValue}
                  />
                  <span className="text-sm" title={colorValue}>
                    {colorValue}
                  </span>
                </div>
              ) : (
                <span className="text-gray-400">-</span>
              );
            } else if (col.key == 'isActive') {
              value = (
                <div className="flex items-center whitespace-nowrap space-x-3 sm:order-1 order-2">
                  <button
                    onClick={() => handleSwitch(item, !item[col.key])}
                    className={`w-10 h-5 flex items-center rounded-full p-1 transition-colors duration-200 ${item[col.key] ? 'bg-primary-600' : 'bg-gray-300'}`}
                  >
                    <div
                      className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform duration-200 ${item[col.key] ? 'translate-x-5' : 'translate-x-0'}`}
                    />
                  </button>
                </div>
              )
            } else if (col.key === "actions") {
              value = (
                <div className="flex space-x-2">
                  <span className="flex gap-1 text-primary-600 cursor-pointer" onClick={() => onAction(item, "edit")}>
                    <Pencil className="w-4 h-4" /> Edit
                  </span>
                  <span className="flex gap-1 text-red-600 cursor-pointer" onClick={() => onAction(item, "delete")}>
                    <Delete className="w-4 h-4" /> Delete
                  </span>
                </div>
              );
            } else {
              const rawValue = item[col.key];
              if (typeof rawValue === "string") {
                value = rawValue ? (
                  <span className="block max-w-[220px] truncate" title={rawValue}>
                    {rawValue}
                  </span>
                ) : (
                  <span className="text-gray-400">-</span>
                );
              } else {
                value = rawValue ?? "-";
              }
            }

            return (
              <td
                key={col.key}
                className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap"
              >
                {value}
              </td>
            );
          })}
        </tr>
      ))}
    </tbody>
  );

  const renderError = () => (
    <tbody className="whitespace-nowrap">
      <tr>
        <td colSpan={columns.length} className="px-6 py-12 text-center text-sm text-gray-500">
          <div className="flex flex-col items-center">
            <Icon name="AlertCircle" size={32} className="text-error mb-2" />
            <p className="font-medium mb-2">{error}</p>
            <button
              onClick={onRetry}
              className="mt-2 px-3 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700"
            >
              Retry
            </button>
          </div>
        </td>
      </tr>
    </tbody>
  );

  const renderEmpty = () => (
    <tbody className="whitespace-nowrap">
      <tr>
        <td colSpan={columns.length} className="px-6 py-12 text-center text-sm text-gray-500">
          <div className="flex flex-col items-center">
            <Icon name="Search" size={32} className="text-gray-400 mb-2" />
            <p className="font-medium mb-2">{emptyMessage}</p>
          </div>
        </td>
      </tr>
    </tbody>
  );

  const renderLoading = () => (
    <tbody className="bg-white divide-y divide-gray-200 whitespace-nowrap">
      {[...Array(3)].map((_, index) => (
        <tr key={index}>
          {columns.map((_, i) => (
            <td key={i} className="px-6 py-4">
              <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );

  return (
    <div className="!overflow-x-auto">
      <table className="w-full">
        {renderHeader()}
        {isLoading
          ? renderLoading()
          : error
            ? renderError()
            : data.length === 0
              ? renderEmpty()
              : renderRows()}
      </table>
    </div>
  );
};

export default DataTable;